import React from 'react';
import { Modal } from 'framework/ui/ui-builder';
import { ModalForm } from 'framework/form';
import VacancyGroupInput from '../input/VacancyGroupInput';
import VacancyInput from '../input/VacancyInput';
import { MapVacancyType, VACANCY_GROUP_STATUS } from 'domain/shared/newVacancy';
import ViewVacancyContent from '../../ViewVacancyContent';
import ChangeRequestsList from '../changeRequests';
import { useVacancyGroupUrl } from 'domain/recruiting/newVacancy/routing';
import { useNavigate } from 'react-router-dom';
import { Typography } from '@mui/material';

interface VacancyViewWrapperProps {
  vacancy: MapVacancyType;
  refreshData: () => Promise<void>;
  moveToGroup: (vacancyId: string, groupId: string | null) => Promise<void>;
  modalsState: Dictionary<boolean>;
  toggleModal: (modalKey: string) => void;
  vacancyGroupId: string;
  vacancies: MapVacancyType[];
}

/**
 * Wrapper component for viewing a vacancy and managing related modals
 */
const VacancyViewWrapper: React.FC<VacancyViewWrapperProps> = ({
  vacancy,
  refreshData,
  moveToGroup,
  modalsState,
  toggleModal,
  vacancyGroupId,
  vacancies,
}) => {
  const navigate = useNavigate();
  const { newDashboard } = useVacancyGroupUrl();

  return (
    <>
      <ViewVacancyContent setGeneralContextData={refreshData} />

      <Modal
        title="Move vacancy to another group"
        isOpen={modalsState.moveToGroup}
        onToggle={() => toggleModal('moveToGroup')}
      >
        <ModalForm
          closeModal={() => toggleModal('moveToGroup')}
          modalOpen={modalsState.moveToGroup}
          submitFormFunc={values => moveToGroup(vacancy.id, values.vacancyGroupId)}
          afterSubmit={{
            execute: () => {
              if (vacancies?.length > 1) {
                return refreshData();
              }
              return navigate(newDashboard);
            },
          }}
        >
          <VacancyGroupInput
            label="Target Vacancy Group"
            source="vacancyGroupId"
            defaultFilter={{
              'specification:vacancyGroupByStatus': {
                in: [VACANCY_GROUP_STATUS.OPEN, VACANCY_GROUP_STATUS.REQUIRES_REVIEW],
              },
            }}
          />
        </ModalForm>
      </Modal>

      <Modal isOpen={modalsState.removeFromGroup} onToggle={() => toggleModal('removeFromGroup')}>
        <ModalForm
          closeModal={() => toggleModal('removeFromGroup')}
          modalOpen={modalsState.removeFromGroup}
          submitFormFunc={() => moveToGroup(vacancy.id, null)}
          afterSubmit={{
            execute: () => {
              if (vacancies?.length > 1) {
                return refreshData();
              }
              return navigate(newDashboard);
            },
          }}
        >
          <Typography variant="body1" component="p">
            Are you sure that you want to remove this vacancy from group?
          </Typography>
        </ModalForm>
      </Modal>

      <Modal isOpen={modalsState.addVacancy} onToggle={() => toggleModal('addVacancy')}>
        <ModalForm
          closeModal={() => toggleModal('addVacancy')}
          modalOpen={modalsState.addVacancy}
          submitFormFunc={values => moveToGroup(values.vacancyId, vacancyGroupId)}
          afterSubmit={{ execute: refreshData }}
        >
          <VacancyInput ignoreVacancyId={vacancy.id} source="vacancyId" placeholder="Enter vacancy title" />
        </ModalForm>
      </Modal>

      <Modal title="Change requests" isOpen={modalsState.changeRequests} onToggle={() => toggleModal('changeRequests')}>
        <ChangeRequestsList vacancyId={vacancy.id} refreshData={refreshData} />
      </Modal>
    </>
  );
};

export default VacancyViewWrapper;
